// Conventional pronunciation of the 24 uniliteral signs of the Kama alphabet
// (src/data/languages/kama/categories/lecture.ts), keyed by transliteration
// letter. Feeds the stèle's engraved label (HieroglyphCard.tsx, design 19d
// « linteau »): "ꜣ  ALEPH · SE LIT « A »". Only ꜣ, ỉ and ꜥ carry a
// traditional letter name in Egyptology; the others just show their reading.
// Kept here rather than parsed out of each entry's translation, whose
// mnemonic tails ("— lu comme un a", "— wou-wou", "— tch !") aren't regular
// enough to extract a sound from.

interface AlphabetSign {
  name?: string;
  sound: { fr: string; en: string };
}

const KAMA_ALPHABET: Record<string, AlphabetSign> = {
  'ꜣ': { name: 'aleph', sound: { fr: 'a', en: 'a' } },
  'ỉ': { name: 'yod', sound: { fr: 'i', en: 'i' } },
  'y': { sound: { fr: 'y', en: 'y' } },
  'ꜥ': { name: 'ayin', sound: { fr: 'a', en: 'a' } },
  'w': { sound: { fr: 'ou', en: 'w' } },
  'b': { sound: { fr: 'b', en: 'b' } },
  'p': { sound: { fr: 'p', en: 'p' } },
  'f': { sound: { fr: 'f', en: 'f' } },
  'm': { sound: { fr: 'm', en: 'm' } },
  'n': { sound: { fr: 'n', en: 'n' } },
  'r': { sound: { fr: 'r', en: 'r' } },
  'h': { sound: { fr: 'h', en: 'h' } },
  'ḥ': { sound: { fr: 'h', en: 'h' } },
  'ḫ': { sound: { fr: 'kh', en: 'kh' } },
  'ẖ': { sound: { fr: 'kh', en: 'kh' } },
  's': { sound: { fr: 's', en: 's' } },
  'š': { sound: { fr: 'ch', en: 'sh' } },
  'q': { sound: { fr: 'q', en: 'q' } },
  'k': { sound: { fr: 'k', en: 'k' } },
  'g': { sound: { fr: 'g', en: 'g' } },
  't': { sound: { fr: 't', en: 't' } },
  'ṯ': { sound: { fr: 'tch', en: 'ch' } },
  'd': { sound: { fr: 'd', en: 'd' } },
  'ḏ': { sound: { fr: 'dj', en: 'j' } },
};

export function getAlphabetSign(letter: string, language: string): { name?: string; sound: string } | null {
  const sign = KAMA_ALPHABET[letter];
  if (!sign) return null;
  return { name: sign.name, sound: language.startsWith('en') ? sign.sound.en : sign.sound.fr };
}
